import { Environment } from "./constants";

export type AuthConfig = {
  clientId: string;
  authority: string;
  ROPCAuthority?: string;
  appId: string;
  csfClientId?: string;
  knownAuthorities?: string[];
};

export const AUTH_SOURCES = ["b2b", "b2c", "vylor_b2b"] as const;
export type AuthSource = (typeof AUTH_SOURCES)[number];

// I would say "B2C", but lints don't like the number.
export const B_TO_C_POLICIES = {
  SIGN_IN: "b2c_1a_granular_signin",
  ROPC: "b2c_1a_granular_ropc",
  REDEEM: "b2c_1a_granular_signup_redeem",
} as const;
export type BToCPolicy = (typeof B_TO_C_POLICIES)[keyof typeof B_TO_C_POLICIES];

// The default B2C policy we use for sign-in.
export const DEFAULT_B_TO_C_POLICY = B_TO_C_POLICIES.SIGN_IN;
export const updateBToCAuthority = (authority: string, policy: BToCPolicy) => {
  return authority.replace(`/${DEFAULT_B_TO_C_POLICY}/`, `/${policy}/`);
};

/**
 * You'd think the point of this file is to export this const, but we really
 * want this to only be accessed through "getAuthConfig", so that we can ensure
 * that B2C policies are always correctly applied.
 *
 * @deprecated prefer `getAuthConfig` except where absolutely necessary.
 */
export const AUTH_CONFIGS: Record<
  Environment | "beta",
  Record<AuthSource, AuthConfig>
> = {
  // TODO: figure out how beta is gonna work.
  beta: {
    b2b: {
      clientId: "b15c7f6d-1162-4f5c-a7ea-f5ea18c5ba81",
      authority:
        "https://login.microsoftonline.com/3e20ecb2-9cb0-4df1-ad7b-914e31dcdda4",
      appId:
        "api://us.dev.insights.granular.ag/b15c7f6d-1162-4f5c-a7ea-f5ea18c5ba81",
    },
    b2c: {
      clientId: "874617ca-0c03-4ea4-acd5-11ba717e609e",
      authority: `https://cortevadbpsandbox.b2clogin.com/cortevadbpsandbox.onmicrosoft.com/${DEFAULT_B_TO_C_POLICY}/`,
      appId: "https://CortevaDbpSandbox.onmicrosoft.com/csf-services",
      csfClientId: "b2810b62-8e4f-4aa6-a328-6c3fa4cda287",
      knownAuthorities: ["cortevadbpsandbox.b2clogin.com"],
    },
    vylor_b2b: {
      clientId: "4743223d-2003-41f4-ae4a-d293810f3616",
      authority:
        "https://login.microsoftonline.com/db505480-8ec6-495c-b579-32c58ed84a01",
      appId: "api://new.dev.sso.granular.ag",
    }
  },
  // Same as development
  local: {
    b2b: {
      clientId: "d81c0885-9a1f-4a12-958d-98da3bd63ce7",
      authority:
        "https://login.microsoftonline.com/3e20ecb2-9cb0-4df1-ad7b-914e31dcdda4",
      appId: "api://dev.granularinsights",
    },
    b2c: {
      clientId: "9ce07e52-c1f3-496b-9633-ef5d8a4a56a2",
      authority: `https://CortevaDbpDev.b2clogin.com/CortevaDbpDev.onmicrosoft.com/${DEFAULT_B_TO_C_POLICY}/`,
      appId: "https://CortevaDbpDev.onmicrosoft.com/csf-services",
      csfClientId: "9c727c49-1b32-4ac1-9569-75391090a220",
      knownAuthorities: ["CortevaDbpDev.b2clogin.com"],
    },
    vylor_b2b: {
      clientId: "4be4c5e1-c4f3-4c84-a127-dc58a28e75cd",
      authority:
        "https://login.microsoftonline.com/db505480-8ec6-495c-b579-32c58ed84a01",
      appId: "api://new.dev.sso.granular.ag",
    }
  },
  development: {
    b2b: {
      clientId: "d81c0885-9a1f-4a12-958d-98da3bd63ce7",
      authority:
        "https://login.microsoftonline.com/3e20ecb2-9cb0-4df1-ad7b-914e31dcdda4",
      appId: "api://dev.granularinsights",
    },
    b2c: {
      clientId: "9ce07e52-c1f3-496b-9633-ef5d8a4a56a2",
      authority: `https://CortevaDbpDev.b2clogin.com/CortevaDbpDev.onmicrosoft.com/${DEFAULT_B_TO_C_POLICY}/`,
      appId: "https://CortevaDbpDev.onmicrosoft.com/csf-services",
      csfClientId: "9c727c49-1b32-4ac1-9569-75391090a220",
      knownAuthorities: ["CortevaDbpDev.b2clogin.com"],
    },
    vylor_b2b: {
      clientId: "4be4c5e1-c4f3-4c84-a127-dc58a28e75cd",
      authority:
        "https://login.microsoftonline.com/db505480-8ec6-495c-b579-32c58ed84a01",
      appId: "api://new.dev.sso.granular.ag",
    }
  },
  test: {
    b2b: {
      clientId: "e8d11743-7ee5-4096-9260-b07247006874",
      authority:
        "https://login.microsoftonline.com/3e20ecb2-9cb0-4df1-ad7b-914e31dcdda4",
      appId: "api://test.granularinsights",
    },
    b2c: {
      clientId: "ccf7029c-8944-4483-8054-e7b774d4b1e2",
      authority: `https://AgCompanyB2CDEV.b2clogin.com/AgCompanyB2CDEV.onmicrosoft.com/${DEFAULT_B_TO_C_POLICY}/`,
      appId: "https://AgCompanyB2CDEV.onmicrosoft.com/csf-services",
      csfClientId: "f30438a8-1cd7-4b6d-a137-410819ce3573",
      knownAuthorities: ["AgCompanyB2CDEV.b2clogin.com"],
    },
    vylor_b2b: {
      clientId: "17a9ff82-5caa-4e13-8e7b-d3a196ca5abc",
      authority:
        "https://login.microsoftonline.com/db505480-8ec6-495c-b579-32c58ed84a01",
      appId: "api://new.test.sso.granular.ag",
    }
  },
  production: {
    b2b: {
      clientId: "cb1d5ab6-ee37-4385-9759-fe47ae3217c7",
      authority:
        "https://login.microsoftonline.com/3e20ecb2-9cb0-4df1-ad7b-914e31dcdda4",
      appId: "api://granularinsights",
    },
    b2c: {
      clientId: "d741fed1-2217-495b-8363-1fdc44c26234",
      authority: `https://agcompanyb2c.b2clogin.com/agcompanyb2c.onmicrosoft.com/${DEFAULT_B_TO_C_POLICY}/`,
      appId: "https://AgCompanyB2C.onmicrosoft.com/csf-services",
      csfClientId: "929f442c-f042-4978-b9a9-d1ccd11e87ff",
      knownAuthorities: ["AgCompanyB2C.b2clogin.com"],
    },
    vylor_b2b: {
      clientId: "064bfa93-3d2d-4bda-af0c-1f7ae46ac801",
      authority:
        "https://login.microsoftonline.com/db505480-8ec6-495c-b579-32c58ed84a01",
      appId: "api://new.sso.granular.ag",
    },
  },
} as const;

/**
 * Retrieves the AuthConfig for the given environment and auth source.
 *
 * If given a B2C policy, it will modify the authority to use that policy.
 */
export const getAuthConfig = (
  env: Environment | "beta",
  source: AuthSource,
  bToCPolicy: BToCPolicy | undefined = undefined,
): AuthConfig => {
  const config = AUTH_CONFIGS[env][source];
  if (source === "b2c" && bToCPolicy && bToCPolicy !== DEFAULT_B_TO_C_POLICY) {
    return {
      ...config,
      authority: updateBToCAuthority(config.authority, bToCPolicy),
    };
  }
  return config;
};

export const getScopesByConfig = (config: AuthConfig): string[] => {
  return [`${config.appId}/internal`, "offline_access", "openid", "profile"];
};
